<template>
  <main class="view inquiries">
    <div class="descriptor">
      <h1>{{ $t('inquiries.headline') }}</h1>
      <p v-if="canViewAll">{{ $t('inquiries.copyManager') }}</p>
      <p v-else>{{ $t('inquiries.copyUser') }}</p>
      <Button @click="createInquiry" primary>{{ $t('inquiries.buttons.newInquiry') }}</Button>
    </div>
    <List
      :filters="computedListFilters"
      :structure="computedStructure"
      :data="computedListData"
      :sort.sync="sort"
      @interaction="handleListInteraction"
      @select="handleListSelect"
      @reload="reload"
      @context="handleListContext"
    />
  </main>
</template>

<script>
  import List from '@/components/ui/list/List';
  import ToggleBar from '@/components/ui/inputs/ToggleBar';
  import Dropdown from '@/components/ui/inputs/Dropdown';
  import TextInput from '@/components/ui/inputs/TextInput';
  import Button from '@/components/ui/inputs/Button';
  import InquiryProgress from '@/components/inquiry/InquiryProgress';
  import InquiryStatusLabel from '@/components/inquiry/InquiryStatusLabel';
  import ContextMenu from '@/components/ui/inputs/ContextMenu';
  import ModalInquiryReview from '@/components/modals/ModalInquiryReview';
  import InquiryActions from '@/objects/InquiryActions';
  import Format from '@/utils/Format';
  import Sort from '@/utils/Sort';
  import { Store } from '@/store';
  import VueUtil from '@/utils/VueUtil';
  import ListInquiriesFirstname from '@/components/inquiries/ListInquiriesFirstname';
  import ModalInquiryReturn from '@/components/modals/ModalInquiryReturn';
  import InquiryStatus from '@/objects/InquiryStatus';
  import ModalInquiryClosed from '@/components/modals/ModalInquiryClosed';
  import ModalInquiryDelete from '@/components/modals/ModalInquiryDelete';
  import ModalInquiryArchive from '@/components/modals/ModalInquiryArchive';
  import ModalInquiryTransfer from '@/components/modals/ModalInquiryTransfer';
  import ModalInquiryDownload from '@/components/modals/ModalInquiryDownload';
  import ModalInquiryReopen from '@/components/modals/ModalInquiryReopen';
  import DBStorage from '../utils/DBStorage';

  export default {
    name: 'Inquiries',
    components: {
      Button,
      ToggleBar,
      List,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {},
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        inquiries: [],
        filters: {},
        sort: {
          property: 'modified',
          ascending: false,
        },
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      computedStructure() {
        const columnLabels = this.$i18n.t('inquiries.list.labels');
        return {
          firstname: {
            label: columnLabels.firstname,
            component: ListInquiriesFirstname,
          },
          lastname: {
            label: columnLabels.lastname,
          },
          inquiryid: {
            label: columnLabels.inquiryid,
          },
          created: {
            label: columnLabels.created,
            format: Format.toLocalizedDate,
            sort: Sort.list.date,
          },
          modified: {
            label: columnLabels.modified,
            format: Format.toLocalizedDate,
            sort: Sort.list.date,
          },
          psychologist: {
            label: columnLabels.psychologist,
            format: v => v?.name || '-',
            sort: Sort.list.psychologist,
          },
          progress: {
            label: columnLabels.progress,
            component: InquiryProgress,
            sortable: false,
          },
          status: {
            label: columnLabels.status,
            component: InquiryStatusLabel,
          },
        };
      },
      computedListFilters() {
        const globalLabels = this.$i18n.t('labels');
        const filterLabels = this.$i18n.t('inquiries.list.filters');
        return [
          ...(Store.permissions.inquiryCanViewAll ? [{
            id: 'inquiries',
            type: ToggleBar,
            props: {
              secondary: true,
              value: 'branch',
              options: this.computedToggles,
            },
          }] : []),
          {
            id: 'status',
            type: Dropdown,
            props: {
              placeholderPrefix: filterLabels.status,
              placeholder: globalLabels.all,
              multiselect: true,
              tertiary: true,
              options: this.computedStatuses,
            },
          },
          ...(Store.permissions.inquiryCanViewAll ? [{
            id: 'psychologist',
            type: Dropdown,
            props: {
              placeholderPrefix: filterLabels.psychologist,
              placeholder: globalLabels.all,
              toggleable: true,
              tertiary: true,
              options: this.computedPsychologists,
            },
          }] : []),
          {
            id: 'search',
            type: TextInput,
            props: {
              id: 'list-search-input',
              icon: 'search',
              placeholder: globalLabels.search,
            },
          },
        ];
      },
      computedToggles() {
        const inquiries = this.inquiries || [];
        const filterLabels = this.$i18n.t('inquiries.list.filters');
        const out = [
          { value: 'assigned', label: filterLabels.inquiries.assigned },
          { value: 'branch', label: filterLabels.inquiries.branch },
        ];

        const unAssigned = inquiries.filter(i => i.psychologist === null);
        if (unAssigned.length > 0) {
          out.push({ value: 'unassigned', label: 'Umteilung benötigt', count: unAssigned.length });
        }
        return out;
      },
      computedStatuses() {
        const statuses = this.$i18n.t('inquiries.statuses');
        const keys = Object.keys(statuses);
        const mapping = keys.map(key => ({
          value: key, label: statuses[key],
        }));
        const out = [];
        mapping.forEach((entry) => {
          const found = out.find(e => e.label === entry.label);
          if (found) {
            const values = found.value.constructor === String ? [found.value] : found.value;
            values.push(entry.value);
            found.value = values;
          } else {
            out.push(entry);
          }
        });
        out.forEach((o) => {
          if (o.value.constructor === Array) {
            // eslint-disable-next-line no-param-reassign
            o.value = o.value.toString();
          }
        });
        return out;
      },
      computedPsychologists() {
        const inquiries = this.inquiries || [];
        const psychologists = [];
        inquiries.forEach((entry) => {
          const psychologist = entry.psychologist;
          if (psychologist) {
            const index = psychologists.findIndex(p => p.value === psychologist.uuid);
            if (index === -1) {
              psychologists.push({ value: psychologist.uuid, label: psychologist.name });
            }
          }
        });
        return psychologists;
      },
      computedListData() {
        const filters = this.filters;
        if (!this.inquiries) {
          return null;
        }
        let out = (this.inquiries || []).slice();
        if (filters.inquiries === 'assigned') {
          out = out.filter(e => e.psychologist?.uuid === Store.uuid);
        }
        if (filters.inquiries === 'unassigned') {
          out = out.filter(e => e.psychologist === null);
        }
        if (filters.psychologist) {
          out = out.filter(e => e.psychologist?.uuid === filters.psychologist);
        }
        if (filters.status?.length > 0) {
          const statuses = [];
          filters.status.forEach((filter) => {
            if (filter.includes(',')) {
              statuses.push(...filter.split(','));
            } else {
              statuses.push(filter);
            }
          });
          out = out.filter(e => statuses.includes(e.status));
        }
        if (filters.search) {
          const search = filters.search.toLowerCase();
          out = out.filter(e => e.firstname.toLowerCase().includes(search)
            || e.lastname.toLowerCase().includes(search)
            || e.inquiryid.includes(search)
            || e.created.includes(search)
            || e.psychologist?.name.toLowerCase().includes(search));
        }
        return out;
      },
      canViewAll() {
        return Store?.permissions?.inquiryCanViewAll || false;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {},
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    created() {
      this.reload();
    },
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      async reload() {
        this.inquiries = null;
        this.inquiries = await this.$sav.api.get('/inquiries');
      },
      createInquiry() {
        this.$router.push('/inquiry');
      },
      async executeAction(action, reference) {
        switch (action) {
          case InquiryActions.REVIEW: {
            const modal = VueUtil.createComponent(ModalInquiryReview, document.body, {
              propsData: { uuid: reference.uuid },
              parent: this,
            });
            modal.$once('done', async () => {
              this.reload();
            });
            break;
          }
          case InquiryActions.RETURN_REVIEW: {
            const modal = VueUtil.createComponent(ModalInquiryReturn, document.body, {
              propsData: { uuid: reference.uuid },
              parent: this,
            });
            modal.$once('done', async () => {
              await DBStorage.delete(reference.uuid);
              this.reload();
            });
            break;
          }
          case InquiryActions.CLOSE: {
            const modal = VueUtil.createComponent(ModalInquiryClosed, document.body, {
              propsData: { uuid: reference.uuid },
              parent: this,
            });
            modal.$once('done', async () => {
              this.reload();
            });
            break;
          }
          case InquiryActions.ARCHIVE: {
            const modal = VueUtil.createComponent(ModalInquiryArchive, document.body, {
              propsData: { uuid: reference.uuid },
              parent: this,
            });
            modal.$once('done', async () => {
              await DBStorage.delete(reference.uuid);
              this.reload();
            });
            break;
          }
          case InquiryActions.REOPEN: {
            const modal = VueUtil.createComponent(ModalInquiryReopen, document.body, {
              propsData: { uuid: reference.uuid },
              parent: this,
            });
            modal.$once('done', async () => {
              this.reload();
            });
            break;
          }
          case InquiryActions.TRANSFER: {
            const modal = VueUtil.createComponent(ModalInquiryTransfer, document.body, {
              propsData: {
                uuid: reference?.uuid,
                isAssigned: reference?.psychologist !== null && reference?.psychologist !== undefined,
              },
              parent: this,
            });
            modal.$once('done', async () => {
              this.reload();
            });
            break;
          }
          case InquiryActions.DOWNLOAD: {
            const modal = VueUtil.createComponent(ModalInquiryDownload, document.body, {
              propsData: { uuid: reference.uuid },
              parent: this,
            });
            modal.$once('done', async () => {
              this.reload();
            });
            break;
          }
          case InquiryActions.DELETE: {
            const modal = VueUtil.createComponent(ModalInquiryDelete, document.body, {
              propsData: { uuid: reference.uuid },
              parent: this,
            });
            modal.$once('done', async () => {
              await DBStorage.delete(reference.uuid);
              this.reload();
            });
            break;
          }
          default: {
            console.log(action, reference);
          }
        }
      },

      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleListInteraction(data) {
        const { id, value } = data;
        const filters = Object.assign({}, this.filters);
        filters[id] = value;
        this.filters = filters;
      },
      handleListSelect(data) {
        const { uuid, status, psychologist } = data;
        if (status === InquiryStatus.REVIEWED) {
          this.$router.push(`/review/${uuid}`);
        } else if (psychologist) {
          this.$router.push(`/inquiry/${uuid}`);
        }
      },
      handleListContext(evt) {
        const { currentTarget, row } = evt;
        const actions = InquiryActions.byStatus(row.status);

        const context = VueUtil.createComponent(ContextMenu, document.body, {
          propsData: {
            origin: currentTarget,
            reference: row,
            actions,
          },
          parent: this,
        });

        context.$once('action', (action, reference) => {
          context.destroy();
          this.executeAction(action, reference);
        });
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.view.inquiries {
}
</style>
